import { useEffect, useMemo, useState } from "react";
import "./RankingTable.css";

const PERIOD_OPTIONS = [
    { value: "all", label: "Depuis toujours" },
    { value: "1y", label: "1 an" },
    { value: "1m", label: "1 mois" },
    { value: "1w", label: "1 semaine" }
];

const LIMIT_OPTIONS = [5, 10, 20, 50, 100];

function getRankClass(rank) {
    if (rank === 1) {
        return "rank-badge rank-badge--gold";
    }

    if (rank === 2) {
        return "rank-badge rank-badge--silver";
    }

    if (rank === 3) {
        return "rank-badge rank-badge--bronze";
    }

    return "rank-badge";
}

function RankingTable({
    title,
    columns,
    fetchRanking,
    defaultLimit = 10,
    defaultPeriod = "all",
    emptyMessage = "Aucune donnée disponible."
}) {
    const [items, setItems] = useState([]);
    const [limit, setLimit] = useState(defaultLimit);
    const [period, setPeriod] = useState(defaultPeriod);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const normalizedColumns = useMemo(() => columns || [], [columns]);

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError("");

            try {
                const data = await fetchRanking({ limit, period });
                setItems(data?.items || []);
            } catch {
                setItems([]);
                setError("Impossible de charger ce classement pour l'instant.");
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [fetchRanking, limit, period]);

    return (
        <div className="card shadow-sm">
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h2 className="h6 mb-0">{title}</h2>
                <div className="d-flex align-items-center gap-2">
                    <select
                        className="form-select form-select-sm ranking-table-select"
                        value={period}
                        onChange={(event) => setPeriod(event.target.value)}
                        aria-label="Période"
                    >
                        {PERIOD_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <select
                        className="form-select form-select-sm ranking-table-select"
                        value={limit}
                        onChange={(event) => setLimit(Number.parseInt(event.target.value, 10))}
                        aria-label="Limite"
                    >
                        {LIMIT_OPTIONS.map((value) => (
                            <option key={value} value={value}>Top {value}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="card-body p-0">
                {error && (
                    <div className="alert alert-warning m-3 py-2 mb-0">{error}</div>
                )}

                <div className="table-responsive">
                    <table className="table table-striped table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: 70 }}>#</th>
                                {normalizedColumns.map((column) => (
                                    <th key={column.key}>{column.label}</th>
                                ))}
                                <th className="text-end">Contrôles</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={normalizedColumns.length + 2} className="text-center py-4">Chargement...</td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={normalizedColumns.length + 2} className="text-center py-4 text-secondary">{emptyMessage}</td>
                                </tr>
                            ) : (
                                items.map((item, index) => (
                                    <tr key={`${item.brand || ""}-${item.model || ""}-${index}`}>
                                        <td>
                                            <span className={getRankClass(index + 1)}>{index + 1}</span>
                                        </td>
                                        {normalizedColumns.map((column) => (
                                            <td key={column.key}>{item[column.key] || "-"}</td>
                                        ))}
                                        <td className="text-end fw-semibold">{item.controls_count}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default RankingTable;
